import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const HOME_NAV = [
  { label: "Services", href: "#services" },
  { label: "Approach", href: "#approach" },
  { label: "Results", href: "#results" },
  { label: "Voices", href: "#voices" },
  { label: "Process Serving", href: "/process-serving" },
];

const PROCESS_NAV = [
  { label: "Managed IT", href: "/" },
  { label: "Services", href: "#ps-services" },
  { label: "Compliance", href: "#ps-compliance" },
  { label: "Coverage", href: "#ps-coverage" },
];

export const Header = ({ onNavigate, currentPath = "/" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const NAV = currentPath === "/process-serving" ? PROCESS_NAV : HOME_NAV;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    onNavigate?.(href);
  };

  const ctaHref = currentPath === "/process-serving" ? "#ps-contact" : "#contact";
  const ctaLabel = currentPath === "/process-serving" ? "Request a serve" : "Start a project";

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "backdrop-blur-2xl bg-[#050505]/70 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="logo-home"
          onClick={() => go("/")}
          className="flex items-center group"
          aria-label="Leonard Technology Inc. home"
        >
          <img
            src="https://customer-assets-v7afamib.emergentagent.net/job_animated-msp-landing/artifacts/ekwx7qvu_dark_logo_white_background.webp"
            alt="Leonard Technology Inc."
            className="h-11 md:h-12 w-auto object-contain invert transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => go(n.href)}
              className="text-sm text-[#A0AEC0] hover:text-white transition-colors duration-300 tracking-wide"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-contact-btn"
            onClick={() => go(ctaHref)}
            className="hidden sm:inline-flex items-center rounded-full bg-[#00E5FF] text-[#050505] px-5 py-2.5 text-sm font-semibold hover:bg-white transition-colors duration-300"
          >
            {ctaLabel}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-white p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden backdrop-blur-2xl bg-[#050505]/95 border-t border-white/10 px-6 py-6 flex flex-col gap-4"
        >
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => go(n.href)}
              className="text-left text-lg text-[#A0AEC0] hover:text-white"
            >
              {n.label}
            </button>
          ))}
          <button
            onClick={() => go(ctaHref)}
            className="mt-2 rounded-full bg-[#00E5FF] text-[#050505] px-5 py-3 font-semibold"
          >
            {ctaLabel}
          </button>
        </div>
      )}
    </motion.header>
  );
};
